import React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  pure,
  branch,
  compose,
  withHandlers,
  renderNothing,
} from 'react-recompose';
// components
import { Table } from '../../../components/table';
import { TitlePanel } from '../../../components/title-panel';
import { EditReport } from '../../../components/edit-report';
import { PageActions } from '../../../components/page-actions';
import { withPromptModal } from '../../../components/edit-report/hocs';
import { StyledTabsMui } from '../../../components/tabs-mui';
import { openModal, closeModal } from '../../../components/modal/actions';
import { openLoader, closeLoader } from '../../../components/loader/actions';
import { transformPropDataFromSelectToString } from '../../../components/edit-report/helpers';
// features
import PC from '../../permission/role-permission';
import { AuthWrapper } from '../../permission/index';
import { startPivotTable } from '../../pivot-table/actions';
import { makeSelectCurrentBranchGuid } from '../../branch/selectors';
import { makeSelectPivotTableTotalCount } from '../../pivot-table/selectors';
import { setExpandedContainerOptions } from '../../expanded-container/actions';
import { makeSelectInitialDataLoadedStatus } from '../../permission/selectors';
import {
  makeSelectAllAvailableCloReferenceTypes,
  makeSelectAvailableReferenceTypesByScope,
} from '../../reference/selectors';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import { withFixedPopover } from '../../../hocs';
import { reportEnhancer } from '../../../report-common';
// icons
import * as I from '../../../svgs';
// ui
import {
  IconWrapper,
  ListWrapper,
  ZOrderWrapper,
} from '../../../ui';
// utilities
import { getOrderEntryRoute } from '../../../utilities/routes';
// feature dispatch-report
import useExportExcel from '../hooks/use-export-excel';
import { getTabs, getListActionsOpt } from './helpers';
import { orderFilterProps } from '../settings/filter-params';
import { cloTelTableSettings } from '../settings/table-settings';
import { withPageActionsHandlers } from './hocs/with-page-actions-handlers';
import { orderColumnSettings as columnSettings } from '../settings/column-settings';
import {
  makeSelectItemList,
  makeSelectTotalCount,
  makeSelectPagination,
  makeSelectUsedReport,
  makeSelectPageVisited,
  makeSelectListLoading,
  makeSelectReportStatus,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
import {
  setReports,
  selectItem,
  printRequest,
  getXMLRequest,
  setUsedReport,
  startCloByTel,
  cancelCLORequest,
  deleteItemRequest,
  setTableTitleSort,
  getItemListRequest,
  createReportRequest,
  updateReportRequest,
  setTableTitleFilter,
  createReferenceRequest,
  resetListAndPagination,
  exportReportDataRequest,
  changeCancelStatusRequest,
  changeDefaultReportRequest,
} from './actions';
//////////////////////////////////////////////////

const enhance = compose(
  withFixedPopover,
  reportEnhancer,
  withPromptModal(orderFilterProps),
  withPageActionsHandlers(),
  withHandlers({
    handleEditReport: (props) => (fields) => {
      const {
        openModal,
        setUsedReport,
        selectedReport,
        requestPending,
        getItemListRequest,
        createReportRequest,
        updateReportRequest,
      } = props;

      const component = (
        <EditReport
          fields={fields}
          setReport={setUsedReport}
          usedReport={selectedReport}
          requestPending={requestPending}
          updateReportRequest={updateReportRequest}
          createReportRequest={createReportRequest}
          onReportSet={() => getItemListRequest(true)}
        />
      );

      const modal = G.getDefaultReportModal(component);

      openModal(modal);
    },
  }),
  branch(
    ({ selectedReport, initialDataLoaded }) => R.or(
      R.not(initialDataLoaded),
      G.isNilOrEmpty(selectedReport),
    ),
    renderNothing,
  ),
  pure,
);

const renderTable = (props) => {
  const {
    loading,
    itemList,
    totalCount,
    selectItem,
    pagination,
    reportList,
    selectedReport,
    titleSortValues,
    tableTitleFilters,
    getItemListRequest,
    handleClickEditIcon,
    handleTableTitleFilter,
  } = props;

  if (R.not(selectedReport)) return null;

  const freezedField = {
    sequence: 1,
    freezed: true,
    reference: false,
    name: 'orderDetails',
  };

  const reportToUse = G.prependFieldToTableReportFields(selectedReport, freezedField);

  const elementActionsComponent = (data) => (
    <AuthWrapper has={[PC.CLO_READ, PC.CLO_WRITE]}>
      <IconWrapper px={12} cursor='pointer' onClick={(e) => handleClickEditIcon(e, data)}>
        {I.threeDots()}
      </IconWrapper>
    </AuthWrapper>
  );

  const allChecked = G.isAllChecked(itemList);

  const rowBgFunction = (guid, item) => G.ifElse(
    R.and(G.isTrue(R.prop(GC.FIELD_LOAD_APPOINTMENT_REQUIRED, item)), selectedReport.showIssues),
    G.getTheme('colors.light.warningBackground'),
    null,
  );

  const tableSettings = G.getTableSettingsWithMaxHeightByConditions({
    reportList,
    selectedReport,
    tableSettings: cloTelTableSettings,
  });

  const data = {
    loading,
    itemList,
    allChecked,
    totalCount,
    pagination,
    rowBgFunction,
    tableSettings,
    columnSettings,
    titleSortValues,
    tableTitleFilters,
    report: reportToUse,
    hasSelectable: true,
    useTableUiWithBg: true,
    handleTableTitleFilter,
    onOptionClick: selectItem,
    withResizableColumns: true,
    useNewTitleFilterInputs: true,
    handleLoadMoreEntities: getItemListRequest,
    renderRightStickedComponent: elementActionsComponent,
    filterProps: R.indexBy(
      R.prop(GC.FIELD_VALUE), transformPropDataFromSelectToString(orderFilterProps),
    ),
  };

  return <Table {...data} />;
};

const OrderListComponent = (props) => {
  const {
    itemList,
    activeTab,
    totalCount,
    handlePrint,
    getXMLRequest,
    startCloByTel,
    startPivotTable,
    handleSetActiveTab,
    handleAddReferenceList,
  } = props;

  const exportFileTypes = useExportExcel(props, orderFilterProps);

  const handleClickTab = (tabIndex) => {
    if (R.equals(tabIndex, 1)) startCloByTel();

    if (R.equals(tabIndex, 2)) startPivotTable(GC.PIVOT_CLO_REPORT);

    handleSetActiveTab(tabIndex);
  };

  const customTitleComponent = (
    <StyledTabsMui
      count={totalCount}
      activeTab={activeTab}
      tabs={getTabs(activeTab)}
      setActiveTab={handleClickTab}
    />
  );

  const listActionsOpt = getListActionsOpt({
    handlePrint,
    getXMLRequest,
    handleAddReferenceList,
  });

  return (
    <ListWrapper>
      <ZOrderWrapper zIndex={2}>
        <TitlePanel
          {...props}
          pt={30}
          pr={25}
          height='auto'
          withCount={true}
          type={GC.CLO_REPORT}
          popperWithCount={true}
          useTableUiWithBg={true}
          hiddenRightFilterInfo={false}
          filterProps={orderFilterProps}
          exportFileTypes={exportFileTypes}
          customTitleComponent={customTitleComponent}
          title={G.getWindowLocale('titles:clo-list', 'CLO List')}
        />
      </ZOrderWrapper>
      <ZOrderWrapper zIndex={1} p='15px 25px'>
        {renderTable(props)}
      </ZOrderWrapper>
      <AuthWrapper has={[PC.CLO_REFERENCE_WRITE]}>
        <PageActions
          listActions={listActionsOpt}
          shadowColor={G.getTheme('createButton.shadowColor')}
          count={R.filter((item) => item.selected, R.or(itemList, [])).length}
          mainAction={{
            permissions: [PC.CLO_WRITE],
            action: () => G.goToRoute(getOrderEntryRoute()),
            text: G.getWindowLocale('actions:create-clo', 'Create CLO'),
          }}
        />
      </AuthWrapper>
    </ListWrapper>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  itemList: makeSelectItemList(state),
  loading: makeSelectListLoading(state),
  totalCount: makeSelectTotalCount(state),
  pagination: makeSelectPagination(state),
  pageVisited: makeSelectPageVisited(state),
  selectedReport: makeSelectUsedReport(state),
  requestPending: makeSelectReportStatus(state),
  reportList: makeSelectAvailableReports(state),
  titleSortValues: makeSelectTitleSortValues(state),
  currentBranchGuid: makeSelectCurrentBranchGuid(state),
  tableTitleFilters: makeSelectTableTitleFilters(state),
  refList: makeSelectAvailableReferenceTypesByScope(state),
  initialDataLoaded: makeSelectInitialDataLoadedStatus(state),
  pivotTableTotalCount: makeSelectPivotTableTotalCount(state),
  cloRefTypes: makeSelectAllAvailableCloReferenceTypes(state),
});

const enhanceWithConnect = compose(
  connect(mapStateToProps, {
    openModal,
    closeModal,
    setReports,
    openLoader,
    selectItem,
    closeLoader,
    printRequest,
    setUsedReport,
    getXMLRequest,
    startCloByTel,
    startPivotTable,
    cancelCLORequest,
    deleteItemRequest,
    setTableTitleSort,
    getItemListRequest,
    createReportRequest,
    updateReportRequest,
    setTableTitleFilter,
    createReferenceRequest,
    resetListAndPagination,
    exportReportDataRequest,
    changeCancelStatusRequest,
    changeDefaultReportRequest,
    setExpandedContainerOptions,
  }),
  enhance,
);

export const Component = enhanceWithConnect(OrderListComponent);

export default enhanceWithConnect(OrderListComponent);
